/* Keep the original glass material within and around the viewport. No content,
   layout, shadows, animation settings or accessibility state is changed. */
(() => {
  'use strict';
  if (window.__odRenderInitialized || !('IntersectionObserver' in window)
    || window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode')) return;
  window.__odRenderInitialized = true;

  const attribute = 'data-od-render-offscreen';
  const selector = [
    '.od-glass', '.od-hero__panel', '.od-brand__media.od-shaped', '.od-brand__copy',
    '.od-product-card', '.od-info-store', '.od-info-store-copy', '.od-info-article',
    '.od-button--glass', '.od-info-store-action--circle', '.od-footer-panel',
    '.od-catalog__sidebar', '.od-catalog__filters', '.od-catalog__filter-toggle',
    '.od-catalog__banner', '.od-category-heading', '.od-pdp__klarna', '.od-pdp__add',
    '.od-product-information__navigation > a', '.od-product-information__panel',
    '.od-product-information__reviews > #judgeme_product_reviews',
    '.cart-page__items', '.cart-page__summary', '.search-page__header',
    '.shopify-policy__container', '.od-technical__button--glass',
    '.dmc--glass .dmc-surface', '.dmc--glass .dmc-secondary',
    '.dmo-intro', '.dmo-tools',
  ].join(',');
  // Floating UI can extend beyond its anchor; keep its sampling permanently intact.
  const excluded = 'od-header,.od-header,dialog,[popover],#cart-drawer,#filters-drawer,.theme-drawer,.search-modal,.predictive-search,.dm-mario-layer';
  const surfaces = new Set();
  const anchors = new Map();
  const groups = new Map();
  let active = false;

  function restore(element) { element.removeAttribute(attribute); }
  function focused(element) {
    return document.activeElement && element.contains(document.activeElement);
  }
  function forget(element) {
    const anchor = anchors.get(element);
    const group = groups.get(anchor);
    group?.delete(element);
    if (group && !group.size) {
      observer.unobserve(anchor);
      groups.delete(anchor);
    }
    anchors.delete(element);
    surfaces.delete(element);
    restore(element);
  }
  const observer = new IntersectionObserver(entries => {
    if (!active) return;
    for (const entry of entries) {
      const group = groups.get(entry.target);
      if (!group) continue;
      for (const element of group) {
        if (!element.isConnected) {
          forget(element);
        } else if (entry.isIntersecting || focused(element)
          || !entry.boundingClientRect.width || !entry.boundingClientRect.height) {
          restore(element);
        } else {
          element.setAttribute(attribute, '');
        }
      }
    }
  }, { rootMargin: '300px', threshold: 0 });

  function observe(element) {
    if (!element.isConnected || surfaces.has(element) || element.closest(excluded)) return;
    surfaces.add(element);
    // Start at full fidelity; the first asynchronous observation may only remove
    // sampling after the browser confirms that the surface is safely offscreen.
    restore(element);
    // Horizontal overflow clips do not inherit rootMargin. Observe the whole
    // row/navigation/deck so its cards and tabs retain glass during interaction.
    const anchor = element.closest('od-carousel,.od-product-information__navigation,.od-technical__navigation')
      || element.closest('.dmc--glass') || element;
    anchors.set(element, anchor);
    if (!groups.has(anchor)) {
      groups.set(anchor, new Set());
      observer.observe(anchor);
    }
    groups.get(anchor).add(element);
  }
  function scan(scope) {
    if (!active || !scope?.querySelectorAll) return;
    if (scope.matches?.(selector)) observe(scope);
    scope.querySelectorAll(selector).forEach(observe);
  }
  function release(scope) {
    for (const element of surfaces) {
      if (element === scope || scope.contains(element)) {
        forget(element);
      }
    }
  }
  // Collection/cart fragments can be replaced without Theme Editor section events.
  // Only child-list changes are observed; our own state attributes never loop.
  const mutations = 'MutationObserver' in window ? new MutationObserver(records => {
    if (!active) return;
    const additions = new Set();
    const removals = new Set();
    for (const record of records) {
      record.addedNodes.forEach(node => { if (node.nodeType === 1) additions.add(node); });
      record.removedNodes.forEach(node => { if (node.nodeType === 1 && !node.isConnected) removals.add(node); });
    }
    removals.forEach(release);
    additions.forEach(scan);
  }) : null;

  function start() {
    if (active || !document.body?.classList.contains('od-site')) return;
    active = true;
    scan(document);
    mutations?.observe(document.body, { childList: true, subtree: true });
  }
  function stop() {
    active = false;
    observer.disconnect();
    observer.takeRecords();
    mutations?.disconnect();
    surfaces.forEach(restore);
    surfaces.clear();
    anchors.clear();
    groups.clear();
  }
  document.addEventListener('focusin', event => {
    // Keyboard/programmatic focus may precede the next intersection delivery.
    for (let element = event.target; element?.removeAttribute; element = element.parentElement) restore(element);
  });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => release(event.target));
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
